












































import GroupedOpenEnds from './GroupedOpenEnds.vue'
import Toggle from '@/components/Toggle.vue'
import { Card, Icon, Button } from '@alpha/ink'
import { Question, Response } from '@/store/modules/survey-store-module'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import * as db from '@shared/types/database'
import { getOpenEnd } from '@/components/Survey/Utilities/SurveyUtilities'
import Tabs from '@/components/Tabs/Tabs.vue'
import {
  getSegmentCount,
  NestedResponses,
  DoubleNestedResponses
} from '@/components/Survey/Utilities/ChartUtilities'
import { capitalize, debounce } from 'lodash-es'
import { QuestionType } from '@shared/types/database'
import api from '@/api'
import store from '@/store'
import * as graphSdk from '@/api/graphql-sdk.generated'
import BroadSearchResults from './BroadSearchResults.vue'

enum SearchType {
  Broad = 'Broad Search',
  Exact = 'Exact Search'
}

@Component({
  components: {
    Card,
    Icon,
    Toggle,
    Button,
    Tabs,
    GroupedOpenEnds,
    BroadSearchResults
  }
})
export default class OpenEndsCard extends Vue {
  @Prop({ required: true }) question!: Question
  @Prop({ required: true }) isSliced!: boolean
  @Prop({ required: true }) isVariant!: boolean
  @Prop({ required: false, default: false }) showSentiment!: boolean
  @Prop({ required: true }) sortedCategories!: string[]

  @Prop({ required: true }) selectedVariantTab!: string
  @Prop({ required: false, default: '' }) selectedPhrase!: string

  @Prop({ required: true, default: () => [] }) filteredResponses!: Response[]
  @Prop({ required: true }) filteredSlicedResponses!: Response[] | NestedResponses | DoubleNestedResponses

  searchTerm = this.selectedPhrase
  loading = false
  searchOptions = [{ title: SearchType.Exact }, { title: SearchType.Broad, beta: true }]
  selectedSearchType: SearchType.Broad | SearchType.Exact = SearchType.Exact
  advancedResults: graphSdk.SearchResponsesQuery['searchResponses'] | null = null
  activeSearchTerm = ''

  get fuzzySearch () {
    return this.selectedSearchType === SearchType.Broad
  }

  get searchHelperText () {
    return this.fuzzySearch ? `Searching for terms related to ${this.searchTerm}` : `Searching for ${this.searchTerm}`
  }

  @Watch('selectedPhrase', { immediate: true })
  setSearchFromTerm () {
    this.setFilterValue(this.selectedPhrase, true)
  }

  debouncedSetFilter = debounce(this.setFilter, 300)

  setFilter () {
    this.activeSearchTerm = this.searchTerm
  }

  setFilterValue (value: string | InputEvent, immediate?: boolean) {
    if (typeof value !== 'string') {
      const currentTarget = value.currentTarget as HTMLInputElement
      value = currentTarget.value
    }
    this.searchTerm = value
    immediate ? this.setFilter() : this.debouncedSetFilter()
  }

  clearSearch () {
    this.setFilterValue('')
    this.advancedResults = null
  }

  get fuzzySearchAndActiveSearchTerm () {
    return [this.fuzzySearch, this.activeSearchTerm]
  }

  @Watch('fuzzySearchAndActiveSearchTerm')
  async executeAdvancedSearch (newVal: [boolean, string], oldVal: [boolean, string]) {
    const [fuzzySearch, searchTerm] = newVal
    if (!fuzzySearch) return
    this.advancedResults = null
    if (!searchTerm) return
    this.loading = true
    let results
    try {
      const { sharedLinkHash } = this.$route.params
      results = await api.searchResponses({
        questionId: this.question.id,
        searchTerm,
        workspaceId: store.state.surveyConfiguration.workspaceId || store.state.workspace.workspace.id,
        sharedLinkHash
      })
      this.advancedResults = results
    } catch (error) {
      console.error('Something went wrong')
      this.advancedResults = null
    } finally {
      this.loading = false
    }
  }

  get hasOpenEnds () {
    return this.openEnds.length > 0
  }

  get openEnds () {
    return this.filteredResponses.map(openEnd => getOpenEnd(openEnd, this.question)).filter(response => !!response)
  }

  get isTabbedWordcloud () {
    return this.question.type === QuestionType.TextArea &&
        (this.isSliced || this.isVariant) &&
        !this.showSentiment
  }

  get openEndsCount (): number {
    let total = this.openEnds.length

    if (this.isSliced && this.isVariant) {
      total = 0
      Object.values(this.openEndResponses as Record<string, NestedResponses>).forEach(segmentResponses => {
        total += getSegmentCount(segmentResponses)
      })
    } else if (this.isTabbedWordcloud) {
      total = (this.openEndResponses as Response[]).length
    }

    return total
  }

  get openEndResponses () {
    const responses = (this.isSliced || this.isVariant) ? this.filteredSlicedResponses : this.filteredResponses
    // Multi select come through as array
    if (Array.isArray(responses)) {
      return this.groupByAnswer(responses)
    }

    if (this.isSliced && this.isVariant) {
      if (!this.selectedVariantTab) return
      const slicedVariantOpenEnds = responses[this.selectedVariantTab] as NestedResponses
      return this.reduceOpenEndResponses(slicedVariantOpenEnds)
    } else {
      return this.reduceOpenEndResponses(responses as NestedResponses)
    }
  }

  reduceOpenEndResponses (responses: NestedResponses) {
    return Object.keys(responses).reduce((acc: Record<string, NestedResponses>, key) => {
      acc[key] = this.groupByAnswer(responses[key] as Response[]) as NestedResponses
      return acc
    }, {})
  }

  groupByAnswer (responses: Response[]): NestedResponses | Response[] {
    if (this.question.type === db.QuestionType.TextArea && !this.showSentiment) return responses
    return responses.reduce((acc: NestedResponses, response) => {
      if (this.question.hasOtherOpt && !this.question.hasExplainOpt) {
        if (response.answer.includes('Other')) {
          acc.Other ? acc.Other.push(response) : acc.Other = [response]
        }
        return acc
      }
      const key = this.showSentiment ? capitalize(response.sentimentLabel) : response.answer
      acc[key] ? acc[key].push(response) : acc[key] = [response]
      return acc
    }, {})
  }
}
